import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Autocomplete, Alert } from "@material-ui/lab";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import LinearScaleIcon from "@mui/icons-material/LinearScale";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";

import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-noconflict/theme-tomorrow_night";

import ReactJson from "react-json-view";
import axios from "axios";
import { Handle } from "react-flow-renderer";
import { toast } from "react-toastify";
import api from "../../../services/api";
import AerosNode, { targetHandleStyle, sourceHandleStyle } from "./AerosNode";
import { nodeSummary } from "../flowRules";
import useAerosModalStyles from "../../../styles/aerosModalStyles";
import { i18n } from "../../../translate/i18n";

function getPaths(obj, prefix = "") {
  let paths = [];
  for (let key in obj) {
    const currentPath = prefix ? `${prefix}.${key}` : key;
    paths.push(currentPath);
    if (obj[key] !== null && typeof obj[key] === "object") {
      paths = paths.concat(getPaths(obj[key], currentPath));
    }
  }
  return paths;
}

const t = (k, o) => i18n.t(`aeros.flow.http.${k}`, o);

const HttpRequestNode = React.memo(({ data, id, selected }) => {
  const m = useAerosModalStyles();
  const theme = useTheme();
  const dark = theme.palette.type !== "light";
  const inputCls = `${m.input} ${m.inputSmall}`;

  const [url, setUrl] = useState(data?.url || "");
  const [method, setMethod] = useState(data?.method || "GET");
  const [requestBody, setRequestBody] = useState(data?.requestBody || "{}");
  const [headersString, setHeadersString] = useState(data?.headersString || "");
  const [queryParams, setQueryParams] = useState(data?.queryParams || []);
  const [saveVariables, setSaveVariables] = useState(data?.saveVariables || []);
  const [reqTimeout, setReqTimeout] = useState(data?.timeout || 10000);
  const [response, setResponse] = useState(null);
  const [jsonPaths, setJsonPaths] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMapping, setShowMapping] = useState(saveVariables && saveVariables.length > 0);
  const [showFullEditor, setShowFullEditor] = useState(false);
  const [showFullResponse, setShowFullResponse] = useState(false);

  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [presetMode, setPresetMode] = useState(false);
  const [loadingPresets, setLoadingPresets] = useState(false);

  useEffect(() => {
    loadPresets();
  }, []);

  const loadPresets = async () => {
    try {
      setLoadingPresets(true);
      // A API já filtra por companyId baseado no token JWT
      const res = await api.get("/preset-webhooks?isActive=true&includeSystem=true");
      setPresets(res.data.presets || []);
    } catch (error) {
      console.error("Erro ao carregar presets:", error);
    } finally {
      setLoadingPresets(false);
    }
  };

  const handlePresetChange = async (event, preset) => {
    if (!preset) {
      setSelectedPreset(null);
      setPresetMode(false);
      return;
    }
    try {
      setSelectedPreset(preset);
      setPresetMode(true);
      const config = preset.configuration;

      setUrl(config.url || "");
      setMethod(config.method || "GET");
      setHeadersString(config.headers ? JSON.stringify(config.headers, null, 2) : "");
      setQueryParams(config.queryParams || []);
      setRequestBody(config.requestBody || "{}");
      setReqTimeout(config.timeout || 30000);

      if (config.responseVariables && config.responseVariables.length > 0) {
        setSaveVariables(config.responseVariables.map((rv) => ({ path: rv.path, variable: rv.variableName })));
        setShowMapping(true);
      }

      data.url = config.url || "";
      data.method = config.method || "GET";
      data.headersString = config.headers ? JSON.stringify(config.headers, null, 2) : "";
      data.queryParams = config.queryParams || [];
      data.requestBody = config.requestBody || "{}";
      data.responseVariables = config.responseVariables || [];
      data.timeout = config.timeout || 30000;
      data.presetId = preset.id;
      data.presetName = preset.name;
      data.presetProvider = preset.provider;
    } catch (error) {
      console.error("Erro ao aplicar preset:", error);
      toast.error(t("presetError"));
    }
  };

  const clearPreset = () => {
    setSelectedPreset(null);
    setPresetMode(false);
    data.presetId = null;
    data.presetName = null;
    data.presetProvider = null;
  };

  const updateNodeData = useCallback(() => {
    data.url = url;
    data.method = method;
    data.requestBody = requestBody;
    data.headersString = headersString;
    data.queryParams = queryParams;
    data.timeout = reqTimeout;

    if (saveVariables && saveVariables.length > 0) {
      data.saveVariables = saveVariables.map((item) => ({ path: item.path, variable: item.variable }));
      data.responseVariables = saveVariables.map((item) => ({ path: item.path, variableName: item.variable }));
    } else {
      data.saveVariables = [];
      data.responseVariables = [];
    }

    if (!data.responseVariables || !Array.isArray(data.responseVariables)) {
      data.responseVariables = data.saveVariables?.map((item) => ({ path: item.path, variableName: item.variable })) || [];
    }
    if (!data.saveVariables || !Array.isArray(data.saveVariables)) {
      data.saveVariables = data.responseVariables?.map((item) => ({ path: item.path, variable: item.variableName })) || [];
    }
  }, [url, method, requestBody, headersString, queryParams, saveVariables, data, reqTimeout]);

  useEffect(() => {
    if (saveVariables && saveVariables.length > 0 && !showMapping) {
      setShowMapping(true);
    }
  }, [saveVariables, showMapping]);

  const hasBody = ["POST", "PUT", "DELETE"].includes(method);

  let customHeaders = {};
  try {
    customHeaders = headersString ? JSON.parse(headersString) : {};
  } catch (err) {}

  useEffect(() => {
    if (response && typeof response === "object") {
      setJsonPaths(getPaths(response));
    } else {
      setJsonPaths([]);
    }
  }, [response]);

  const buildUrlWithParams = useCallback(
    (baseUrl = url) => {
      if (!queryParams.length) return baseUrl;
      const processedParams = queryParams.map((param) => {
        let value = param.value;
        if (window.flowVariables && value.includes("${")) {
          const regex = /\${([^}]+)}/g;
          value = value.replace(regex, (match, varName) =>
            window.flowVariables[varName] !== undefined ? window.flowVariables[varName] : match
          );
        }
        return { key: param.key, value };
      });
      const queryString = processedParams
        .filter((param) => param.key && param.value)
        .map((param) => `${encodeURIComponent(param.key)}=${encodeURIComponent(param.value)}`)
        .join("&");
      return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    },
    [url, queryParams]
  );

  const addQueryParam = useCallback(() => setQueryParams([...queryParams, { key: "", value: "" }]), [queryParams]);
  const updateQueryParam = useCallback(
    (index, field, value) => {
      const newParams = [...queryParams];
      newParams[index][field] = value;
      setQueryParams(newParams);
    },
    [queryParams]
  );
  const removeQueryParam = useCallback((index) => setQueryParams(queryParams.filter((_, i) => i !== index)), [queryParams]);

  const addSaveVariable = useCallback(() => setSaveVariables([...saveVariables, { path: "", variable: "" }]), [saveVariables]);
  const updateSaveVariable = useCallback(
    (index, field, value) => {
      const newVars = [...saveVariables];
      newVars[index][field] = value;
      setSaveVariables(newVars);
    },
    [saveVariables]
  );
  const removeSaveVariable = useCallback((index) => setSaveVariables(saveVariables.filter((_, i) => i !== index)), [saveVariables]);

  useEffect(() => {
    updateNodeData();
  }, [url, method, requestBody, headersString, queryParams, saveVariables, reqTimeout, updateNodeData]);

  const testRequest = useCallback(async () => {
    try {
      let processedUrl = url;
      if (window.flowVariables && url.includes("${")) {
        const regex = /\${([^}]+)}/g;
        processedUrl = url.replace(regex, (match, varName) =>
          window.flowVariables[varName] !== undefined ? window.flowVariables[varName] : match
        );
      }
      let processedRequestBody;
      const finalUrl = buildUrlWithParams(processedUrl);
      const config = {
        method,
        url: finalUrl,
        headers: { "Content-Type": "application/json", ...customHeaders },
        timeout: reqTimeout,
      };

      if (hasBody) {
        try {
          processedRequestBody = requestBody;
          if (window.flowVariables && requestBody.includes("${")) {
            const regex = /\${([^}]+)}/g;
            processedRequestBody = requestBody.replace(regex, (match, varName) =>
              window.flowVariables[varName] !== undefined ? JSON.stringify(window.flowVariables[varName]) : match
            );
          }
          config.data = JSON.parse(processedRequestBody || "{}");
        } catch (err) {
          config.data = processedRequestBody;
        }
      }

      const res = await axios(config);
      setResponse(res.data);

      if (window.setFlowVariable) {
        window.setFlowVariable("apiResponse", res.data);
      }

      if (saveVariables.length > 0) {
        const variablesToProcess = [...saveVariables];
        const responseVariables = variablesToProcess.map((item) => ({ path: item.path, variableName: item.variable }));

        for (let index = 0; index < responseVariables.length; index++) {
          const extractor = responseVariables[index];
          if (extractor && extractor.path && extractor.variableName) {
            const parts = extractor.path.split(".");
            let valueToSave = res.data;
            let pathValid = true;
            for (let part of parts) {
              if (valueToSave && typeof valueToSave === "object" && part in valueToSave) {
                valueToSave = valueToSave[part];
              } else {
                pathValid = false;
                break;
              }
            }
            if (pathValid && valueToSave !== undefined && valueToSave !== null) {
              data[extractor.variableName] = valueToSave;
              if (window.setFlowVariable) {
                window.setFlowVariable(extractor.variableName, valueToSave);
              } else {
                if (!window.flowVariables) window.flowVariables = {};
                window.flowVariables[extractor.variableName] = valueToSave;
              }
              window.dispatchEvent(
                new CustomEvent("flowVariableUpdate", { detail: { name: extractor.variableName, value: valueToSave } })
              );
            }
          }
        }

        data.responseVariables = responseVariables;
        data.saveVariables = variablesToProcess;
        updateNodeData();
      }
    } catch (error) {
      setResponse(error.response ? error.response.data : t("requestError"));
      if (window.setFlowVariable) {
        window.setFlowVariable("apiError", error.response ? error.response.data : error.message);
      }
    }
  }, [url, buildUrlWithParams, method, requestBody, customHeaders, hasBody, saveVariables, reqTimeout, data, updateNodeData]);

  const systemPreset = presetMode && selectedPreset?.isSystem;
  const aceProps = {
    mode: "json",
    theme: dark ? "tomorrow_night" : "github",
    showPrintMargin: false,
    showGutter: true,
    highlightActiveLine: true,
    width: "100%",
    value: requestBody,
    onChange: (value) => setRequestBody(value),
    setOptions: { useWorker: false, tabSize: 2 },
    style: { borderRadius: 8, border: `1px solid ${theme.palette.divider}` },
  };
  const jsonTheme = dark ? "ocean" : "rjv-default";
  const responseBox = {
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    fontSize: 11,
    backgroundColor: theme.palette.aeros.surface,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: 8,
  };
  const row = { display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 6, alignItems: "center" };
  const field = (props) => <TextField className={inputCls} size="small" variant="outlined" fullWidth {...props} />;

  return (
    <AerosNode
      id={id}
      type="httpRequest"
      selected={selected}
      width={340}
      hasTarget={false}
      hasSource={false}
      extra={
        <>
          {/* ids "httpRequest-in"/"httpRequest-out" preservados do nó antigo */}
          <Handle type="target" position="left" id="httpRequest-in" style={targetHandleStyle()} />
          <Handle type="source" position="right" id="httpRequest-out" style={sourceHandleStyle()} />
          <div className="nodrag" style={{ padding: "0 12px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              <label className={m.fieldLabel}>{t("presets")}</label>
              <Autocomplete
                options={presets}
                getOptionLabel={(option) => `${option.name} (${option.provider})`}
                value={selectedPreset}
                onChange={handlePresetChange}
                loading={loadingPresets}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} className={inputCls} variant="outlined" placeholder={t("presetPlaceholder")} />
                )}
                renderOption={(option) => (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{option.name}</div>
                    <div className={m.hint} style={{ marginTop: 0 }}>{option.description}</div>
                    <div style={{ marginTop: 4, display: "flex", gap: 4 }}>
                      <Chip label={option.provider.toUpperCase()} size="small" variant="outlined" color="primary" />
                      {option.isSystem && <Chip label={t("presetSystem")} size="small" variant="outlined" />}
                    </div>
                  </div>
                )}
                clearOnEscape
              />
              {presetMode && selectedPreset && (
                <Alert
                  severity="info"
                  style={{ marginTop: 6 }}
                  action={
                    <Button size="small" onClick={clearPreset}>
                      {t("removePreset")}
                    </Button>
                  }
                >
                  {t("usingPreset", { name: selectedPreset.name })}
                </Alert>
              )}
            </div>

            <div>
              <label className={m.fieldLabel}>{presetMode ? t("urlPreset") : t("url")}</label>
              {field({
                value: url,
                onChange: (e) => {
                  setUrl(e.target.value);
                  data.url = e.target.value;
                },
                placeholder: "https://api.exemplo.com/webhook",
                disabled: systemPreset,
              })}
              {presetMode && <div className={m.hint}>{t("urlPresetHint")}</div>}
            </div>

            <div>
              <label className={m.fieldLabel}>{t("method")}</label>
              <Autocomplete
                options={["GET", "POST", "PUT", "DELETE"]}
                value={method}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setMethod(newValue);
                    data.method = newValue;
                  }
                }}
                size="small"
                renderInput={(params) => <TextField {...params} className={inputCls} variant="outlined" />}
                disabled={systemPreset}
                clearOnEscape
              />
            </div>

            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<PlayArrowIcon />}
              onClick={testRequest}
              disabled={!url}
              style={{ textTransform: "none" }}
            >
              {t("test")}
            </Button>

            {response && (
              <div>
                <label className={m.fieldLabel}>{t("response")}</label>
                <div style={{ ...responseBox, maxHeight: 150, overflow: "auto" }}>
                  {typeof response === "object" ? (
                    <ReactJson
                      src={response}
                      theme={jsonTheme}
                      displayDataTypes={false}
                      displayObjectSize={false}
                      enableClipboard={false}
                      collapsed={1}
                      name={false}
                      style={{ backgroundColor: "transparent" }}
                    />
                  ) : (
                    String(response)
                  )}
                </div>
                <button type="button" className={m.linkButton} style={{ marginTop: 6 }} onClick={() => setShowFullResponse(true)}>
                  {t("fullResponse")}
                </button>
              </div>
            )}

            <button type="button" className={m.linkButton} style={{ marginLeft: 0 }} onClick={() => setShowAdvanced(!showAdvanced)}>
              <SettingsIcon style={{ fontSize: 14 }} />
              {showAdvanced ? t("hideAdvanced") : t("showAdvanced")}
            </button>

            {showAdvanced && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {presetMode && <Alert severity="warning">{t("advancedWarning")}</Alert>}

                <div>
                  <label className={m.fieldLabel}>{t("timeout")}</label>
                  {field({
                    type: "number",
                    value: reqTimeout,
                    onChange: (e) => setReqTimeout(parseInt(e.target.value) || 10000),
                    inputProps: { min: 1000, max: 60000 },
                  })}
                </div>

                <div>
                  <label className={m.fieldLabel}>{t("queryParams")}</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {queryParams.map((param, index) => (
                      <div style={row} key={index}>
                        {field({ placeholder: t("key"), value: param.key, onChange: (e) => updateQueryParam(index, "key", e.target.value) })}
                        {field({ placeholder: t("value"), value: param.value, onChange: (e) => updateQueryParam(index, "value", e.target.value) })}
                        <IconButton size="small" onClick={() => removeQueryParam(index)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                    <button type="button" className={m.linkButton} style={{ marginLeft: 0 }} onClick={addQueryParam}>
                      <AddCircleOutlineIcon style={{ fontSize: 14 }} />
                      {t("addParam")}
                    </button>
                  </div>
                </div>

                <div>
                  <label className={m.fieldLabel}>{t("headers")}</label>
                  {field({
                    multiline: true,
                    minRows: 2,
                    value: headersString,
                    onChange: (e) => setHeadersString(e.target.value),
                    placeholder: '{"Authorization": "Bearer token", "Custom-Header": "XYZ"}',
                  })}
                </div>

                {hasBody && (
                  <div>
                    <label className={m.fieldLabel}>{t("body")}</label>
                    <div style={{ position: "relative" }}>
                      <AceEditor {...aceProps} name={`requestBodyEditor-${id}`} fontSize={12} height="100px" />
                      <IconButton
                        size="small"
                        onClick={() => setShowFullEditor(true)}
                        style={{ position: "absolute", right: 4, top: 4 }}
                      >
                        <OpenInFullIcon fontSize="small" />
                      </IconButton>
                    </div>
                    <Dialog open={showFullEditor} onClose={() => setShowFullEditor(false)} maxWidth="md" fullWidth>
                      <DialogTitle disableTypography style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 16, fontWeight: 700 }}>{t("editBody")}</span>
                        <IconButton onClick={() => setShowFullEditor(false)}>
                          <CloseIcon />
                        </IconButton>
                      </DialogTitle>
                      <DialogContent>
                        <AceEditor {...aceProps} name={`requestBodyEditorFull-${id}`} fontSize={14} height="400px" />
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={() => setShowFullEditor(false)}>{t("close")}</Button>
                      </DialogActions>
                    </Dialog>
                  </div>
                )}
              </div>
            )}

            <button type="button" className={m.linkButton} style={{ marginLeft: 0 }} onClick={() => setShowMapping(!showMapping)}>
              <LinearScaleIcon style={{ fontSize: 14 }} />
              {showMapping ? t("mapHide") : t("mapShow")}
            </button>

            {showMapping && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label className={m.fieldLabel} style={{ marginBottom: 0 }}>{t("mapTitle")}</label>
                {saveVariables.map((variable, index) => (
                  <div style={row} key={index}>
                    <Autocomplete
                      options={jsonPaths}
                      freeSolo
                      size="small"
                      value={variable.path}
                      onChange={(_, newValue) => updateSaveVariable(index, "path", newValue || "")}
                      onInputChange={(_, newValue, reason) => reason === "input" && updateSaveVariable(index, "path", newValue)}
                      renderInput={(params) => (
                        <TextField {...params} className={inputCls} variant="outlined" placeholder={t("pathPlaceholder")} />
                      )}
                    />
                    {field({
                      placeholder: t("variablePlaceholder"),
                      value: variable.variable,
                      onChange: (e) => updateSaveVariable(index, "variable", e.target.value),
                    })}
                    <IconButton size="small" onClick={() => removeSaveVariable(index)} style={{ color: "#F87171" }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </div>
                ))}
                <button type="button" className={m.linkButton} style={{ marginLeft: 0 }} onClick={addSaveVariable}>
                  <AddCircleOutlineIcon style={{ fontSize: 14 }} />
                  {t("addVariable")}
                </button>

                <div className={m.hint} style={{ alignItems: "flex-start" }}>
                  <TipsAndUpdatesIcon style={{ color: "#FBBF24", marginTop: 2 }} />
                  <div>
                    <div style={{ fontWeight: 600 }}>{t("tips")}</div>
                    <div>• {t("tipResponse")}</div>
                    <div>• {t("tipVariables")}</div>
                  </div>
                </div>
              </div>
            )}

            <Dialog open={showFullResponse} onClose={() => setShowFullResponse(false)} maxWidth="lg" fullWidth>
              <DialogTitle disableTypography style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 16, fontWeight: 700 }}>{t("fullResponseTitle")}</span>
                <IconButton onClick={() => setShowFullResponse(false)}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
                {response && typeof response === "object" ? (
                  <ReactJson
                    src={response}
                    theme={jsonTheme}
                    displayDataTypes
                    displayObjectSize
                    enableClipboard
                    collapsed={false}
                    name="response"
                    style={{ ...responseBox, padding: 15, fontFamily: undefined }}
                  />
                ) : (
                  <div style={{ ...responseBox, padding: 16 }}>{String(response)}</div>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowFullResponse(false)}>{t("close")}</Button>
              </DialogActions>
            </Dialog>
          </div>
        </>
      }
    >
      {nodeSummary({ type: "httpRequest", data: { method, url } })}
    </AerosNode>
  );
});

export default HttpRequestNode;
